/**
 * @fileoverview Results summary component for displaying analysis results overview
 */

import React from 'react';
import { EditOutlined } from '@ant-design/icons';
import TrebbleCard from '../../common/TrebbleCard.tsx';
import TrebbleButton from '../../common/TrebbleButton.tsx';
import TrebbleBadge from '../../common/TrebbleBadge.tsx';
import { TrebbleTitle, TrebbleText, TrebbleParagraph } from '../../common/TrebbleTypography.tsx';
import TrebbleSpace from '../../common/TrebbleSpace.tsx';
import TrebbleDivider from '../../common/Divider.js';

const { Title, Text, Paragraph } = { Title: TrebbleTitle, Text: TrebbleText, Paragraph: TrebbleParagraph };

/**
 * @typedef {import('../types/deepcut.js').AnalysisResults} AnalysisResults
 */

/**
 * Results summary component for displaying analysis results overview
 * @param {Object} props - Component props
 * @param {AnalysisResults} props.results - Analysis results to display
 * @param {Function} props.onEditPlan - Callback for editing the plan
 * @returns {React.ReactElement}
 */
export default function ResultsSummary({ results, onEditPlan }) {
  const getBadgeColor = (hits) => {
    if (hits > 50) return "red";
    if (hits > 20) return "orange";
    return "blue";
  };

  return (
    <TrebbleCard>
      <TrebbleSpace direction="vertical" size="large" style={{ width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Title level={3} style={{ margin: 0 }}>Analysis Results</Title>
          <TrebbleButton 
            type="primary" 
            icon={<EditOutlined />}
            onClick={onEditPlan}
          >
            Edit Plan
          </TrebbleButton>
        </div>

        {/* Selected Intent */}
        <div>
          <Title level={4}>Selected Intent</Title>
          <TrebbleBadge 
            count={results.selectedIntent}
            style={{ backgroundColor: 'var(--trebble-primary)' }}
          />
        </div>

        {/* Strategy Summary */}
        <div>
          <Title level={4}>Strategy Summary</Title>
          <TrebbleCard style={{ backgroundColor: '#f0f8ff' }}>
            <Paragraph style={{ whiteSpace: 'pre-line', margin: 0 }}>
              {results.strategySummary}
            </Paragraph>
          </TrebbleCard>
        </div>

        {/* Editing Plan Summary */}
        <div>
          <Title level={4}>Editing Plan Summary</Title>
          <TrebbleSpace direction="vertical" size="small" style={{ width: '100%' }}>
            {results.plan.map((chip) => (
              <div key={chip.chipLabel} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <TrebbleSpace>
                  <Text>{chip.displayLabel}</Text>
                  <TrebbleBadge 
                    count={`${chip.hits} instances`}
                    color={getBadgeColor(chip.hits)}
                  />
                </TrebbleSpace>
                <TrebbleBadge 
                  count="remove"
                  color="red"
                />
              </div>
            ))}
          </TrebbleSpace>
        </div>

        {/* Questions */}
        {results.probingQuestions.length > 0 && (
          <div>
            <Title level={4}>Strategic Questions ({results.probingQuestions.length})</Title>
            <TrebbleSpace direction="vertical" size="small" style={{ width: '100%' }}>
              {results.probingQuestions.map((question, index) => (
                <Text key={question.id} type="secondary">
                  {index + 1}. {question.text}
                </Text>
              ))}
            </TrebbleSpace>
          </div>
        )}
      </TrebbleSpace>
    </TrebbleCard>
  );
} 